/*                                                                                                        */
/* Component that renders a range slider input field with a datalist. It can also be used outside formik  */
/* Route: '/commons/:commonsSlug/quest/:questSlug/:routeStopSlug'                                         */
/*                                                                                                        */
import { FormikProps } from 'formik/dist/types';
import { useEffect, useMemo, useState } from 'react';

interface CustomDatalistOptionProps {
  option: {
    value: number,
    label: string,
    /** Show or hide tick option on small screen */
    showOnMobile: boolean
  },
  isActive?: boolean,
}

/**
 * @param option:  An object containing a value and a label for the option element.
 * @param isActive:  A boolean indicating whether the option is selected or not.
 * @returns:  an option element wrapped by span, which should be used within a datalist of CustomRange
 */
export const CustomDatalistOption = ({ option, isActive }: CustomDatalistOptionProps) => {
  return (
    <span className={`${isActive && 'active'} ${!option.showOnMobile ? 'd-none d-md-flex' : 'd-flex'}`}>
      {/* <option/> has highly limited styling. that is why we wrap it with span   https://developer.mozilla.org/en-US/docs/Web/HTML/Element/option#styling_with_css */}
      <option value={option.value} label={option.label} />
    </span>
  );
};
interface CustomRangeProps {
  min: number;
  max: number;
  step: number;
  /** customize step for mobile layout, value equal to step if not specified */
  stepForMobileTickMark?: number;
  defaultValue?: number
  /** Show or hide tick label; defaults to true if not specified */
  showTickLabel?: boolean;
  /** to use with formik */
  fieldName?: string;
  /** to use with formik */
  fieldValue?: number;
  /** to use with formik */
  setFieldValue?: FormikProps<number>['setFieldValue'];
  /** to use with formik */
  setTouched?: FormikProps<{ [x: string]: boolean; }>['setTouched'];
  className?: string;
  /** custom onChange function to use outside of formik, e.g. update state  */
  handleOnChange?: (value: number) => void;
  /** used to customize non numeric labels, the key of the object should be set to the value where the label should be located, the value of the object should corresponding to the label to be displayed*/
  strLabels?: { [index: number]: string },
}

/** A range slider that can be used in a form or on its own. */
const CustomRange = ({
  fieldName,
  fieldValue,
  min,
  max,
  step = 1,
  stepForMobileTickMark = step,
  defaultValue = 0,
  setFieldValue,
  className,
  setTouched,
  showTickLabel = true,
  handleOnChange,
  strLabels,
}: CustomRangeProps) => {

  // in case the field is not controlled by formik, we save the value in a state
  const [value, setValue] = useState(fieldValue || defaultValue);
  useEffect(() => {
    if (fieldValue !== undefined) {
      setValue(fieldValue);
    }
  }, [fieldValue]);

  // Inline style for the slider background
  const percentageToHighlight = ((value - min) / (max - min)) * 100;
  const rangeBackground = {
    background: `linear-gradient(to right, #60b6aa ${percentageToHighlight}%, #dee2e6 ${percentageToHighlight}%)`,
  };

  const dataListOptions = useMemo(() => {

    // Sequence generator function (commonly referred to as "range", e.g. Clojure, PHP, etc.), returns an array of numbers
    const range = (start: number, stop: number, step: number) => Array.from({ length: Math.ceil((stop - start) / step + 1) }, (_, i) => start + i * step);

    const optionsMobile = range(min, max, stepForMobileTickMark);
    const optionsDefault = range(min, max, step);

    const dataListOptions = optionsDefault.map((optionValue) => {
      return {
        value: optionValue,
        label: showTickLabel
          ?
          strLabels
            ? (strLabels[optionValue] || '') // in case strLabels array does not include all the possible values, the values that are not included in the array will not have a label
            : optionValue.toString()
          : '',
        showOnMobile: optionsMobile.includes(optionValue)
      }
    });

    return dataListOptions;
  }, [min, max, step, stepForMobileTickMark, showTickLabel, strLabels]);

  return (
    <div className={'pb-3 ' + className}>
      <input
        name={fieldName}
        type='range'
        className='custom-range'
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(event) => {
          const newValue = +event.target.value;
          setFieldValue
            ? setFieldValue(fieldName, newValue) // in formik
            : setValue(newValue); // standalone
          setTouched && setTouched({ [fieldName]: true });
          handleOnChange && handleOnChange(newValue)
        }
        }
        onTouchStart={(e) => { e.currentTarget.focus() }} // for range slide to be focusable to mobile 
        list='range-list'
        style={{ ...rangeBackground }}
      />
      <datalist className='d-flex justify-content-between text-small' id='range-list' style={{ padding: '0px 5px' }}>
        {dataListOptions.map((option, index) => {
          return (
            <CustomDatalistOption
              key={index}
              option={option}
              isActive={value === option.value && fieldValue !== undefined} // when fieldValue is undefined (e.g. set by form initial value), range slider default value is set to 0, in this case, we don't want to highlight the corresponding datalist option
            />
          );
        })}
      </datalist>

    </div>
  );
};

export default CustomRange;
